import { Divider, SvgIcon, Typography } from '@mui/joy';
import { Network } from '@phosphor-icons/react';
import { useCallback, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { z } from 'zod';

import {
  LocalizationFeature,
  SaveCancelButtons,
  SideTabMenu,
  useLocalizationForm,
  WarningChip,
} from './MultiLanguageModal';

import { useTenantSlug } from '../tenant/helpers';

import { Input, InputField } from '~/components/ui/input';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import { LocalizationService } from '~/services/api/localization/endpoints';
import QueryResolver from '~/services/api/query-resolver';
import { useWrappedMutation } from '~/services/api/rest-api';
import { getErrorMessage } from '~/services/api/rest-api-error';
import { useIntakeChannel } from '~/slices/intakeChannelSlice';
import { useForm } from '~/utils/forms';

export function TranslateReportForm() {
  const intl = useIntl();
  const selectedChannel = useIntakeChannel().intakeChannelData.selectedChannel;
  const slug = useTenantSlug();

  const reportTypeQuery = LocalizationService.endpoints.reportType.useQuery(
    {
      selectedChannel: selectedChannel ?? 0,
      slug: slug,
    },
    { skip: !slug || !selectedChannel },
  );
  const departmentQuery = LocalizationService.endpoints.departments.useQuery(
    {
      selectedChannel: selectedChannel ?? 0,
      slug: slug,
    },
    { skip: !slug || !selectedChannel },
  );
  const reportCustomFieldQuery =
    LocalizationService.endpoints.reportCustomFields.useQuery(
      {
        selectedChannel: selectedChannel ?? 0,
        slug: slug,
      },
      { skip: !slug || !selectedChannel },
    );
  const reportCustomFieldConditionalLogicQuery =
    LocalizationService.endpoints.reportCustomFieldConditionalLogics.useQuery(
      {
        selectedChannel: selectedChannel ?? 0,
        slug: slug,
      },
      { skip: !slug || !selectedChannel },
    );
  const localizedChannelQuery =
    LocalizationService.endpoints.otherChannelData.useQuery(
      {
        selectedChannel: selectedChannel ?? 0,
        slug: slug,
      },
      { skip: !slug || !selectedChannel },
    );

  const form = useLocalizationForm();
  const isOriginal = form.watch('is_original');
  const selectedLanguage = form.watch('selected_language');
  const selectedFeature = form.watch('selected_feature');
  const selectedCustomField = form.watch('selected_custom_field');
  const selectedConditionalLogicField = form.watch(
    'selected_custom_field_conditional_logic',
  );
  const isEditing = form.watch('is_editing');
  const setSelectedFeature = (val: LocalizationFeature) =>
    form.setValue('selected_feature', val);
  const setSelectedCustomField = (val: number) =>
    form.setValue('selected_custom_field', val);
  const setSelectedConditionalLogicField = (val: number) =>
    form.setValue('selected_custom_field_conditional_logic', val);

  if (!selectedLanguage)
    return (
      <div className='flex justify-center'>
        <FormattedMessage defaultMessage='To view the translation of a language, kindly select a language first' />
      </div>
    );

  return (
    <div className='flex flex-row gap-4'>
      <div className='basis-96 flex flex-col gap-4 overflow-y-auto max-h-[700px] pr-2'>
        <WarningChip>
          <FormattedMessage defaultMessage="Changes made on the translated version won't affect the default language added for the report form" />
        </WarningChip>
        <SideTabMenu
          title={intl.formatMessage({ defaultMessage: 'Report Form Settings' })}
          description={intl.formatMessage({
            defaultMessage:
              'View and make changes to the translated version of Out of scope message and Success Message',
          })}
          isActive={
            selectedFeature === LocalizationFeature.REPORT_FORM_SETTTINGS
          }
          disabled={isEditing}
          onClick={() =>
            setSelectedFeature(LocalizationFeature.REPORT_FORM_SETTTINGS)
          }
        />
        <SideTabMenu
          title={intl.formatMessage({ defaultMessage: 'Report Type Choices' })}
          description={intl.formatMessage({
            defaultMessage:
              'View and make changes to the translated version of the added report types',
          })}
          isActive={selectedFeature === LocalizationFeature.REPORT_TYPE}
          disabled={isEditing}
          onClick={() => setSelectedFeature(LocalizationFeature.REPORT_TYPE)}
        />
        <SideTabMenu
          title={intl.formatMessage({ defaultMessage: 'Department' })}
          description={intl.formatMessage({
            defaultMessage:
              'View and make changes to the translated version of the added departments',
          })}
          isActive={selectedFeature === LocalizationFeature.DEPARTMENT}
          disabled={isEditing}
          onClick={() => setSelectedFeature(LocalizationFeature.DEPARTMENT)}
        />
        <QueryResolver query={reportCustomFieldQuery}>
          {(customFields) =>
            customFields.map((customField) => (
              <SideTabMenu
                key={customField.id}
                title={customField.title}
                description={intl.formatMessage({
                  defaultMessage:
                    'View and make changes to the translated version of the added custom text field',
                })}
                isActive={
                  selectedFeature === LocalizationFeature.CUSTOM_FIELD &&
                  selectedCustomField === customField.id
                }
                disabled={isEditing}
                onClick={() => {
                  setSelectedFeature(LocalizationFeature.CUSTOM_FIELD);
                  setSelectedCustomField(customField.id);
                }}
              />
            ))
          }
        </QueryResolver>
        <QueryResolver query={reportCustomFieldConditionalLogicQuery}>
          {(conditionalLogic) =>
            conditionalLogic.map((field) => (
              <SideTabMenu
                key={field.id}
                title={field.label}
                titleIconRight={
                  <SvgIcon
                    component={Network}
                    fontSize='md'
                    color='primary'
                    inheritViewBox
                  />
                }
                description={intl.formatMessage({
                  defaultMessage:
                    'View and make changes to the translated version of the added custom field\'s conditional logic',
                })}
                isActive={
                  selectedFeature ===
                    LocalizationFeature.CUSTOM_FIELD_CONDITIONAL_LOGIC &&
                  selectedConditionalLogicField === field.id
                }
                disabled={isEditing}
                onClick={() => {
                  setSelectedFeature(
                    LocalizationFeature.CUSTOM_FIELD_CONDITIONAL_LOGIC,
                  );
                  setSelectedConditionalLogicField(field.id);
                }}
              />
            ))
          }
        </QueryResolver>
      </div>
      <Divider orientation='vertical' />
      <div className='flex-1 overflow-y-auto h-[700px]'>
        {selectedFeature === LocalizationFeature.REPORT_FORM_SETTTINGS && (
          <QueryResolver query={localizedChannelQuery}>
            {(reportFormData) => {
              if (reportFormData[0] === undefined)
                return (
                  <Typography>
                    <FormattedMessage defaultMessage='No Tenant Data Found' />
                  </Typography>
                );
              let translatedData = undefined;
              Object.entries(reportFormData[0]?.translations).forEach(
                ([, value]) => {
                  if (value.language_id === selectedLanguage) {
                    translatedData = value;
                  }
                },
              );
              if (!translatedData || isOriginal)
                translatedData = {
                  success_report_submit_message: '',
                  out_of_scope_message: '',
                  id: null,
                };
              if (selectedFeature === LocalizationFeature.REPORT_FORM_SETTTINGS)
                return (
                  <FormSettingsTranslation
                    key={`${selectedLanguage}-${isOriginal}-form-setting`}
                    defaultValues={{
                      language_id: selectedLanguage,
                      id: translatedData.id,
                      main_id: reportFormData[0].id,
                      translated_out_of_scope_message:
                        translatedData.out_of_scope_message,
                      translated_success_report_submit_message:
                        translatedData.success_report_submit_message,
                      success_report_submit_message:
                        reportFormData[0].success_report_submit_message,
                      out_of_scope_message:
                        reportFormData[0].out_of_scope_message,
                    }}
                  />
                );
            }}
          </QueryResolver>
        )}
        {selectedFeature === LocalizationFeature.REPORT_TYPE && (
          <QueryResolver query={reportTypeQuery}>
            {(data) => (
              <ReportTypeDepartmentLocalization
                key={`${selectedLanguage}-${isOriginal}`}
                type='report_type'
                defaultValues={{
                  language_id: selectedLanguage,
                  data: data.map(({ id, name, translations }) => {
                    let translatedData = undefined;
                    Object.entries(translations).forEach(([, value]) => {
                      if (value.language_id === selectedLanguage) {
                        translatedData = value;
                      }
                    });
                    if (!translatedData)
                      translatedData = {
                        id: null,
                        name: '',
                      };
                    return {
                      name,
                      id: translatedData.id,
                      translated_name: translatedData.name,
                      main_id: id,
                      is_updated: false,
                    };
                  }),
                }}
              />
            )}
          </QueryResolver>
        )}
        {selectedFeature === LocalizationFeature.DEPARTMENT && (
          <QueryResolver query={departmentQuery}>
            {(data) => (
              <ReportTypeDepartmentLocalization
                key={`${selectedLanguage}-${isOriginal}`}
                type='department'
                defaultValues={{
                  language_id: selectedLanguage,
                  data: data.map(({ id, name, translations }) => {
                    let translatedData = undefined;
                    Object.entries(translations).forEach(([, value]) => {
                      if (value.language_id === selectedLanguage) {
                        translatedData = value;
                      }
                    });

                    if (!translatedData)
                      translatedData = {
                        name: '',
                        id: null,
                      };
                    return {
                      id: translatedData.id,
                      main_id: id,
                      name,
                      translated_name: translatedData.name,
                      is_updated: false,
                    };
                  }),
                }}
              />
            )}
          </QueryResolver>
        )}
        {
          <QueryResolver query={reportCustomFieldQuery}>
            {(customFields) => {
              return customFields.map((customField) => {
                if (
                  selectedFeature === LocalizationFeature.CUSTOM_FIELD &&
                  selectedCustomField === customField.id
                ) {
                  let translatedData = undefined;
                  Object.entries(customField.translations).forEach(
                    ([, value]) => {
                      if (value.language_id === selectedLanguage) {
                        translatedData = value;
                      }
                    },
                  );
                  if (!translatedData)
                    translatedData = {
                      title: '',
                      dropdown_choices: [],
                      id: null,
                    };
                  return (
                    <CustomFieldLocalization
                      key={`${selectedLanguage}-${isOriginal}-${selectedCustomField}`}
                      defaultValues={{
                        language_id: selectedLanguage,
                        data: {
                          id: translatedData.id,
                          main_id: customField.id,
                          dropdown_choices: customField.dropdown_choices,
                          title: customField.title,
                          translated_dropdown_choices:
                            translatedData.dropdown_choices,
                          translated_title: translatedData.title,
                        },
                      }}
                    />
                  );
                }
                return null;
              });
            }}
          </QueryResolver>
        }
        {
          <QueryResolver query={reportCustomFieldConditionalLogicQuery}>
            {(conditionalLogic) => {
              return conditionalLogic.map((field) => {
                if (
                  selectedFeature ===
                    LocalizationFeature.CUSTOM_FIELD_CONDITIONAL_LOGIC &&
                  selectedConditionalLogicField === field.id
                ) {
                  let translatedData = undefined;
                  Object.entries(field.translations).forEach(([, value]) => {
                    if (value.language_id === selectedLanguage) {
                      translatedData = value;
                    }
                  });
                  if (!translatedData)
                    translatedData = {
                      label: '',
                      dropdown_choices: [],
                      id: null,
                    };
                  return (
                    <ConditionalLogicFieldLocalization
                      key={`${selectedLanguage}-${isOriginal}-${selectedConditionalLogicField}`}
                      defaultValues={{
                        language_id: selectedLanguage,
                        data: {
                          id: translatedData.id,
                          main_id: field.id,
                          title: field.label,
                          translated_title: translatedData.label,
                        },
                      }}
                    />
                  );
                }
                return null;
              });
            }}
          </QueryResolver>
        }
      </div>
    </div>
  );
}

const formSettingSchema = z.object({
  language_id: z.number(),
  id: z.number().nullable(),
  main_id: z.number(),
  out_of_scope_message: z.string(),
  translated_out_of_scope_message: z.string(),
  success_report_submit_message: z.string(),
  translated_success_report_submit_message: z.string(),
});

type FormSettingsData = z.infer<typeof formSettingSchema>;

function FormSettingsTranslation({
  defaultValues,
}: {
  readonly defaultValues: FormSettingsData;
}) {
  const intl = useIntl();
  const slug = useTenantSlug();
  const selectedChannel = useIntakeChannel().intakeChannelData.selectedChannel;
  const { createToastNotification } = useToastNotificationContext();

  const localizationForm = useLocalizationForm();
  const isOriginal = localizationForm.watch('is_original');
  const isEditing = localizationForm.watch('is_editing');

  const setChannelObjId = useCallback(
    (val: number) => localizationForm.setValue('channel_object_id', val),
    [localizationForm],
  );

  const setIsEditing = () =>
    localizationForm.setValue('is_editing', !isEditing);

  useEffect(() => {
    setChannelObjId(defaultValues.main_id);
  }, [defaultValues.main_id, setChannelObjId]);

  const updateKeypoints = useWrappedMutation(
    LocalizationService.endpoints.updateTenantTranslation.useMutation(),
  );

  const form = useForm({
    defaultValues,
    schema: formSettingSchema,
  });

  const onSubmitHandler = form.handleSubmit(async (data) => {
    await updateKeypoints.mutateAsync(
      {
        selectedChannel: selectedChannel ?? 0,
        slug: slug,
        payload: {
          main_id: data.main_id,
          language_id: data.language_id,
          success_report_submit_message:
            data.translated_success_report_submit_message,
          out_of_scope_message: data.translated_out_of_scope_message,
        },
      },
      {
        onSuccess: () => {
          createToastNotification(
            intl.formatMessage({
              defaultMessage:
                'Report Settings localization updated successfully',
            }),
            'success',
          );
          setIsEditing();
        },
        onError: (error) => {
          createToastNotification(getErrorMessage(error), 'error');
        },
      },
    );
  });

  return (
    <div>
      <SaveCancelButtons
        onSave={onSubmitHandler}
        isLoading={updateKeypoints.state.isLoading}
        isEditable={defaultValues.id !== null}
        warningText={intl.formatMessage({
          defaultMessage:
            'No Editable Message Template Translation found, please click auto translate first to continue',
        })}
      />
      <div className='flex flex-col gap-2 pt-4'>
        <Typography level='title-md'>
          <FormattedMessage defaultMessage='Out of Scope Message' />
        </Typography>
        {isEditing ? (
          <InputField
            size='lg'
            field='translated_out_of_scope_message'
            control={form.control}
          />
        ) : (
          <Typography className='pl-2'>
            {isOriginal
              ? form.watch('out_of_scope_message')
              : form.watch('translated_out_of_scope_message') ?? '--'}
          </Typography>
        )}
        <Typography level='title-md'>
          <FormattedMessage defaultMessage='Success Message' />
        </Typography>
        {isEditing ? (
          <InputField
            size='lg'
            field='translated_success_report_submit_message'
            control={form.control}
          />
        ) : (
          <Typography className='pl-2'>
            {isOriginal
              ? form.watch('success_report_submit_message')
              : form.watch('translated_success_report_submit_message') ?? '--'}
          </Typography>
        )}
      </div>
    </div>
  );
}

const schema = z.object({
  language_id: z.number(),
  data: z.array(
    z.object({
      id: z.number().nullable(),
      main_id: z.number(),
      name: z.string(),
      translated_name: z.string(),
      is_updated: z.boolean(),
    }),
  ),
});

type LocalizationData = z.infer<typeof schema>;

function ReportTypeDepartmentLocalization({
  defaultValues,
  type,
}: {
  readonly defaultValues: LocalizationData;
  readonly type: 'department' | 'report_type';
}) {
  const intl = useIntl();
  const selectedChannel = useIntakeChannel().intakeChannelData.selectedChannel;
  const slug = useTenantSlug();
  const { createToastNotification } = useToastNotificationContext();
  const updateReportType = useWrappedMutation(
    LocalizationService.endpoints.updateReportTypeTranslation.useMutation(),
  );
  const updateDepartment = useWrappedMutation(
    LocalizationService.endpoints.updateDepartmentTranslation.useMutation(),
  );

  const localizedForm = useLocalizationForm();
  const isEditing = localizedForm.watch('is_editing');
  const isOriginal = localizedForm.watch('is_original');
  const setIsEditing = () => localizedForm.setValue('is_editing', !isEditing);
  const setDepartmentIds = useCallback(
    (val: number[]) => localizedForm.setValue('department_ids', val),
    [localizedForm],
  );
  const setReportTypeIds = useCallback(
    (val: number[]) => localizedForm.setValue('report_type_ids', val),
    [localizedForm],
  );

  useEffect(() => {
    if (type === 'report_type') {
      setReportTypeIds(defaultValues.data.map((data) => data.main_id));
    }
    if (type === 'department') {
      setDepartmentIds(defaultValues.data.map((data) => data.main_id));
    }
  }, [type, defaultValues, setReportTypeIds, setDepartmentIds]);

  const form = useForm({
    defaultValues,
    schema,
  });

  const localizationData = form.watch('data');

  const onSubmitHandler = form.handleSubmit(async (formData) => {
    const updatedTranslations = formData.data.filter(
      (data) => data.main_id !== null && data.is_updated,
    );
    const promises = updatedTranslations.map((translation) => {
      if (!translation.id) return;
      const payload = {
        selectedChannel: selectedChannel ?? 0,
        slug: slug,
        payload: {
          id: translation.id,
          name: translation.translated_name,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore
          main_id: translation.main_id,
          language_id: formData.language_id,
        },
      };

      if (type === 'department') {
        return updateDepartment.mutateAsync(payload);
      }
      if (type === 'report_type') {
        updateReportType.mutateAsync(payload);
      }
      createToastNotification(
        intl.formatMessage({ defaultMessage: 'data updated successfully' }),
        'success',
      );
    });
    await Promise.all(promises);
    setIsEditing();
  });

  return (
    <div>
      <SaveCancelButtons
        isLoading={updateReportType.state.isLoading}
        onSave={onSubmitHandler}
        isEditable={defaultValues.data.every((item) => item.id != null)}
      />
      <div className='flex flex-col gap-2 mt-8 overflow-y-auto h-[620px]'>
        {localizationData.map((localization) => (
          <div key={localization.id}>
            {!isEditing ? (
              <Typography>
                {isOriginal ? localization.name : localization.translated_name}
              </Typography>
            ) : (
              <Input
                value={localization.translated_name}
                disabled={!localization.main_id}
                onChange={(e) => {
                  const updatedTranslations = [...localizationData];
                  updatedTranslations.forEach((updatedTranslation) => {
                    if (updatedTranslation.id === localization.id) {
                      updatedTranslation.translated_name = e.target.value;
                      updatedTranslation.is_updated = true;
                    }
                  });
                  form.setValue('data', updatedTranslations);
                }}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const fieldSchema = z.object({
  language_id: z.number(),
  data: z.object({
    id: z.number().nullable(),
    main_id: z.number(),
    title: z.string(),
    translated_title: z.string(),
  }),
});
const dropdownFieldSchema = z.object({
  language_id: z.number(),
  data: z.object({
    id: z.number().nullable(),
    main_id: z.number(),
    title: z.string(),
    translated_title: z.string(),
    dropdown_choices: z.array(z.string()),
    translated_dropdown_choices: z.array(z.string()),
  }),
});

type CustomFieldLocalizationData = z.infer<typeof dropdownFieldSchema>;
type ConditionalLogicFieldLocalizationData = z.infer<typeof fieldSchema>;

function CustomFieldLocalization({
  defaultValues,
}: {
  readonly defaultValues: CustomFieldLocalizationData;
}) {
  const intl = useIntl();
  const selectedChannel = useIntakeChannel().intakeChannelData.selectedChannel;
  const slug = useTenantSlug();
  const { createToastNotification } = useToastNotificationContext();
  const updateCustomField = useWrappedMutation(
    LocalizationService.endpoints.updateReportCustomFieldTranslation.useMutation(),
  );

  const localizedForm = useLocalizationForm();
  const isEditing = localizedForm.watch('is_editing');
  const isOriginal = localizedForm.watch('is_original');
  const setIsEditing = () => localizedForm.setValue('is_editing', !isEditing);

  const form = useForm({
    defaultValues,
    schema: dropdownFieldSchema,
  });

  const translatedChoices = form.watch('data.translated_dropdown_choices');
  const choices = form.watch('data.dropdown_choices');

  const onSubmitHandler = form.handleSubmit(async (formData) => {
    if (formData.data.id === null) return;
    await updateCustomField.mutateAsync(
      {
        selectedChannel: selectedChannel ?? 0,
        slug: slug,
        payload: {
          id: formData.data.id,
          title: formData.data.translated_title,
          dropdown_choices: formData.data.translated_dropdown_choices,
          main_id: formData.data.main_id,
          language_id: formData.language_id,
        },
      },
      {
        onSuccess: () => {
          createToastNotification(
            intl.formatMessage({ defaultMessage: 'data updated successfully' }),
            'success',
          );
          setIsEditing();
        },
        onError: (error) => {
          const customError = getErrorMessage(error);
          createToastNotification(customError, 'error');
        },
      },
    );
  });

  return (
    <div>
      <SaveCancelButtons
        isLoading={updateCustomField.state.isLoading}
        onSave={onSubmitHandler}
        isEditable={defaultValues.data.id !== null}
      />
      <div className='flex flex-col gap-2 mt-8'>
        <Typography level='title-sm'>
          <FormattedMessage defaultMessage='Title' />
        </Typography>
        {isEditing ? (
          <InputField control={form.control} field='data.translated_title' />
        ) : (
          <Typography>
            {isOriginal
              ? form.watch('data.title')
              : form.watch('data.translated_title')}
          </Typography>
        )}
        {choices.length > 0 && (
          <Typography level='title-sm'>
            <FormattedMessage defaultMessage='Items' />
          </Typography>
        )}
        {isEditing ? (
          translatedChoices.map((choice, key) => (
            <Input
              key={`${choice}-${key}`}
              value={choice}
              onChange={(e) => {
                const updatedChoices = translatedChoices;
                updatedChoices[key] = e.target.value;
                form.setValue(
                  'data.translated_dropdown_choices',
                  updatedChoices,
                );
              }}
            />
          ))
        ) : (
          <div>
            {isOriginal
              ? choices.map((choice, key) => (
                  <Typography key={`${choice}-${key}`}>{choice}</Typography>
                ))
              : translatedChoices.map((choice, key) => (
                  <Typography key={`${choice}-${key}`}>{choice}</Typography>
                ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ConditionalLogicFieldLocalization({
  defaultValues,
}: {
  readonly defaultValues: ConditionalLogicFieldLocalizationData;
}) {
  const intl = useIntl();
  const selectedChannel = useIntakeChannel().intakeChannelData.selectedChannel;
  const slug = useTenantSlug();
  const { createToastNotification } = useToastNotificationContext();
  const updateConditionalLogicField = useWrappedMutation(
    LocalizationService.endpoints.updateReportCustomFieldConditionalLogicTranslation.useMutation(),
  );

  const localizedForm = useLocalizationForm();
  const isEditing = localizedForm.watch('is_editing');
  const isOriginal = localizedForm.watch('is_original');
  const setIsEditing = () => localizedForm.setValue('is_editing', !isEditing);

  const form = useForm({
    defaultValues,
    schema: fieldSchema,
  });

  const onSubmitHandler = form.handleSubmit(async (formData) => {
    if (formData.data.id === null) return;
    await updateConditionalLogicField.mutateAsync(
      {
        selectedChannel: selectedChannel ?? 0,
        slug: slug,
        payload: {
          id: formData.data.id,
          label: formData.data.translated_title,
          main_id: formData.data.main_id,
          language_id: formData.language_id,
        },
      },
      {
        onSuccess: () => {
          createToastNotification(
            intl.formatMessage({ defaultMessage: 'data updated successfully' }),
            'success',
          );
          setIsEditing();
        },
        onError: (error) => {
          const customError = getErrorMessage(error);
          createToastNotification(customError, 'error');
        },
      },
    );
  });

  return (
    <div>
      <SaveCancelButtons
        isLoading={updateConditionalLogicField.state.isLoading}
        onSave={onSubmitHandler}
        isEditable={defaultValues.data.id !== null}
      />
      <div className='flex flex-col gap-2 mt-8'>
        <Typography level='title-sm'>
          <FormattedMessage defaultMessage='Title' />
        </Typography>
        {isEditing ? (
          <InputField control={form.control} field='data.translated_title' />
        ) : (
          <Typography>
            {isOriginal
              ? form.watch('data.title')
              : form.watch('data.translated_title')}
          </Typography>
        )}
      </div>
    </div>
  );
}
